import React, { useEffect, useState } from 'react';
import {
  parseUrgentDeadline,
  shouldBlinkUrgent,
  urgentListRowClass,
} from '../../utils/urgentDeadlineUtils';

/** Ticks once per second while a deadline is set; returns whether the urgent UI should blink. */
export const useUrgentBlink = (deadlineAt, estimateMinutes) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!parseUrgentDeadline(deadlineAt)) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [deadlineAt]);

  return shouldBlinkUrgent(deadlineAt, estimateMinutes, now);
};

/** Red list row: solid until remaining ≤ estimate, then blinks. */
export const UrgentRowShell = ({
  deadlineAt,
  estimateMinutes,
  className = '',
  children,
  as: Component = 'div',
  ...rest
}) => {
  const blink = useUrgentBlink(deadlineAt, estimateMinutes);
  return (
    <Component className={`${urgentListRowClass(blink)} ${className}`} {...rest}>
      {children}
    </Component>
  );
};

/**
 * Red Urgent tag. Blinks only when remaining time is within the allocated estimate
 * (or default 15 min if no estimate).
 */
const UrgentTag = ({
  deadlineAt,
  estimateMinutes,
  className = '',
  forceBlink = null,
}) => {
  const autoBlink = useUrgentBlink(deadlineAt, estimateMinutes);
  const blink = forceBlink == null ? autoBlink : Boolean(forceBlink);

  return (
    <span
      className={`inline-flex items-center gap-0.5 max-w-[8rem] min-h-[1.375rem] px-1.5 py-0.5 rounded-md text-[10px] font-bold leading-tight truncate border border-red-400 bg-red-600 text-white dark:border-red-500 dark:bg-red-700 ${
        blink ? 'wl-urgent-blink' : ''
      } ${className}`}
      title="Urgent"
    >
      <span aria-hidden="true">🚨</span>
      <span>Urgent</span>
    </span>
  );
};

export default UrgentTag;
